import { useState } from "react";
import AdminCard from "./AdminCard";

interface Props { onSave: () => void; }

const defaultProducts = [
  {
    id: 1,
    name: "Social Housing Structured Product",
    category: "UK Structured Product",
    targetReturn: "10.00% p.a.",
    frequency: "Monthly",
    term: "5 Years",
    minInvestment: "£10,000",
    currency: "GBP",
    listing: "UK SPV",
    status: "Open",
    badge: "Flagship",
    description: "A 5-year, asset-backed structured product targeting 10.00% per annum, distributed monthly. Secured by first legal charges over UK assisted living properties.",
    rating: "First Legal Charge",
  },
  {
    id: 2,
    name: "NASDAQ Dubai Sovereign Bond",
    category: "NASDAQ Dubai",
    targetReturn: "8.50% p.a.",
    frequency: "Semi-Annual",
    term: "3 Years",
    minInvestment: "$50,000",
    currency: "USD",
    listing: "NASDAQ Dubai",
    status: "Open",
    badge: "Exchange Listed",
    description: "A USD-denominated bond listed on NASDAQ Dubai, offering semi-annual coupon payments backed by sovereign-grade assets.",
    rating: "Exchange Listed",
  },
  {
    id: 3,
    name: "DFM Listed Property Bond",
    category: "DFM Listed",
    targetReturn: "7.75% p.a.",
    frequency: "Quarterly",
    term: "4 Years",
    minInvestment: "AED 100,000",
    currency: "AED",
    listing: "DFM",
    status: "Coming Soon",
    badge: "DFM Listed",
    description: "A Dirham-denominated property bond listed on the Dubai Financial Market, targeting quarterly distributions backed by UAE real estate assets.",
    rating: "DFM Listed",
  },
];

export default function AdminProducts({ onSave }: Props) {
  const [products, setProducts] = useState(defaultProducts);
  const [expandedId, setExpandedId] = useState<number | null>(1);
  const [flagshipHeadline, setFlagshipHeadline] = useState("Social Housing Structured Product");
  const [flagshipReturn, setFlagshipReturn] = useState("10.00%");
  const [flagshipSub, setFlagshipSub] = useState("5-Year term · UK Assisted Living Asset-Backed · First Legal Charge Security · Monthly Distributions");

  const updateProduct = (id: number, k: string, v: string) => {
    setProducts((prev) => prev.map((p) => p.id === id ? { ...p, [k]: v } : p));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>Investment Products</h1>
        <p className="text-slate-500 text-sm mt-1">Manage the flagship callout and all product cards shown on the homepage.</p>
      </div>

      <AdminCard title="Flagship Product Callout">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Product Name</label>
            <input value={flagshipHeadline} onChange={(e) => setFlagshipHeadline(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Target Return Display</label>
              <input value={flagshipReturn} onChange={(e) => setFlagshipReturn(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Sub-description</label>
            <input value={flagshipSub} onChange={(e) => setFlagshipSub(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
        </div>
      </AdminCard>

      <AdminCard title="Product Cards">
        <div className="space-y-4">
          {products.map((p) => (
            <div key={p.id} className="border border-slate-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                className="w-full flex items-center justify-between px-5 py-4 bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${p.status === "Open" ? "bg-green-400" : "bg-amber-400"}`} />
                  <span className="text-sm font-semibold text-[#071A2E]">{p.name}</span>
                  <span className="text-xs text-slate-400 hidden sm:inline">· {p.category}</span>
                </div>
                <i className={expandedId === p.id ? "ri-arrow-up-s-line text-slate-400 leading-none" : "ri-arrow-down-s-line text-slate-400 leading-none"} />
              </button>

              {expandedId === p.id && (
                <div className="px-5 py-5 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Product Name</label>
                      <input value={p.name} onChange={(e) => updateProduct(p.id, "name", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Category</label>
                      <input value={p.category} onChange={(e) => updateProduct(p.id, "category", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Target Return</label>
                      <input value={p.targetReturn} onChange={(e) => updateProduct(p.id, "targetReturn", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Income Frequency</label>
                      <input value={p.frequency} onChange={(e) => updateProduct(p.id, "frequency", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Term</label>
                      <input value={p.term} onChange={(e) => updateProduct(p.id, "term", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Min. Investment</label>
                      <input value={p.minInvestment} onChange={(e) => updateProduct(p.id, "minInvestment", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Currency</label>
                      <input value={p.currency} onChange={(e) => updateProduct(p.id, "currency", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Status</label>
                      <select value={p.status} onChange={(e) => updateProduct(p.id, "status", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] cursor-pointer">
                        <option>Open</option>
                        <option>Coming Soon</option>
                        <option>Closed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Badge Label</label>
                      <input value={p.badge} onChange={(e) => updateProduct(p.id, "badge", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Security / Rating</label>
                      <input value={p.rating} onChange={(e) => updateProduct(p.id, "rating", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Description</label>
                    <textarea value={p.description} onChange={(e) => updateProduct(p.id, "description", e.target.value)} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </AdminCard>

      <div className="flex justify-end">
        <button onClick={onSave} className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#071A2E] text-white text-sm font-semibold hover:bg-[#0D2B4A] transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-save-line leading-none" />
          Save Changes
        </button>
      </div>
    </div>
  );
}
